import { useEffect, useRef, useState } from 'react';
import PosLandingOrderSummary from './PosLandingOrderSummary';
import PosLandingDetailsForm from './PosLandingDetailsForm';
import PosLandingStaffStep from './PosLandingStaffStep';
import PosCheckoutTabPending from './PosCheckoutTabPending';
import PosLandingDemoBooking from './PosLandingDemoBooking';
import PosLandingTick from './PosLandingTick';
import { useCheckoutTab } from '../hooks/useCheckoutTab';
import { useConfig } from '../context/ConfigContext';
import {
  submitDemoRequest, submitTrialRequest, signupPaidStaffCount, signupStaffLimit,
} from '../utils/posSignupUtil';
import { formatClockMinutes, formatLongDate, toDateString } from '../utils/posLandingUtil';
import { sendSignupReceivedEmail } from '../utils/emailServiceUtil';
import { describeWriteError } from '../utils/firebaseErrorUtil';
import { POS_SIGNUP_PAY_WINDOW_SECONDS } from '../constants/posConst';

const BLANK_CONTACT = { name: '', business: '', email: '', phone: '' };

/** The paid sign-up's steps, in order — the same three as a till's Subscribe. */
const PAID_STEPS = [
  { key: 'details', label: 'Your details' },
  { key: 'staff', label: 'Staff accounts' },
  { key: 'pay', label: 'Checkout' },
];

/**
 * The checkout dialog opened from the pricing card.
 *
 * A priced plan runs details → staff accounts → payment → done, the same
 * steps as a till's Subscribe. The staff step (PosLandingStaffStep) names
 * the people who will sign in: the plan's free accounts first, the rest
 * charged at the add-on price. The payment happens in a NEW
 * BROWSER TAB (PosLandingPayment at /checkout/<session>), the court-booking
 * system's checkout flow (hooks/useCheckoutTab): this dialog hands it the
 * order and waits on PosCheckoutTabPending; the tab saves the subscription
 * request with its receipt, reports back and closes itself, and this dialog
 * shows the thank-you. The free trial runs details → done, with nothing
 * to pay, and saves a trial request. A custom (Enterprise) plan runs straight
 * to the demo-call booking and saves a demo request. All land on
 * /admin -> Sign-up Requests.
 *
 * Closes on the × or Escape — never on a backdrop tap, which would throw
 * away a half-filled form (CLAUDE.md > Behavior). Neither works while a save
 * is in flight. Closing it closes a checkout tab still open from it.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {object|null} props.plan
 * @param {{extraStaff: number, warehouse: number}} props.addons
 * @param {{extraStaffPrice: number, warehousePrice: number}} props.prices
 * @param {() => void} props.onClose
 * @returns {JSX.Element|null}
 */
function PosLandingSubscribeModal({
  open, plan, addons, prices, onClose,
}) {
  // 'details' | 'staff' | 'pay' | 'call' | 'done'
  const [step, setStep] = useState('details');
  const [contact, setContact] = useState(BLANK_CONTACT);
  // The staff step's rows, as typed; empty until the step first opens.
  const [staffRows, setStaffRows] = useState([]);
  // When the checkout tab's payment window closes; null while not paying.
  const [deadline, setDeadline] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const lastFocus = useRef(null);
  const doneButtonRef = useRef(null);

  const planId = plan?.id || '';
  const planIsCustom = plan?.isCustom === true;
  const planIsTrial = plan?.isTrial === true;
  // Only a plan with staff accounts to give — free ones, or paid add-ons —
  // has a staff step; any other paid plan goes from details straight to pay.
  const hasStaffStep = !planIsTrial && !planIsCustom
    && ((plan?.freeStaff || 0) > 0 || plan?.allowAddons === true);

  const { config } = useConfig();

  const checkoutTab = useCheckoutTab({
    // Submitted in the checkout tab, which is closing: thank them, and send
    // the "we received it" emails from here — this tab stays open, the
    // checkout tab does not. Not awaited: the thank-you should not wait on
    // an email, and the server ignores a repeat for the same request id.
    onSubmitted: (payload) => {
      if (payload?.request && !payload.emailSent) {
        sendSignupReceivedEmail(payload.request, config, { keepalive: true });
      }
      setDone({
        title: 'Subscription request received',
        heading: 'Thank you for letting us be part of your business',
        message: 'We have your payment receipt and your details. Our admins will verify the payment and email your account details and login link once it clears. Thank you for letting us be part of how your business runs.',
      });
      setContact(BLANK_CONTACT);
      setDeadline(null);
      setStep('done');
    },
    // The × in the checkout tab: back to the details, nothing sent.
    onCancelled: () => { setDeadline(null); setStep(hasStaffStep ? 'staff' : 'details'); },
    // The payment window ran out in the checkout tab.
    onExpired: () => {
      setDeadline(null);
      setError('Your checkout expired before the receipt was submitted. Continue again when you are ready to pay.');
      setStep(hasStaffStep ? 'staff' : 'details');
    },
  });
  const endCheckout = checkoutTab.end;

  // Fresh every time it opens, on the step the plan calls for. Keyed on the
  // plan's id rather than the object: the plan list is live, and an
  // unrelated edit on /admin must not bounce a visitor back to step one.
  useEffect(() => {
    if (!open || !planId) return undefined;
    lastFocus.current = document.activeElement;
    setStep(planIsCustom ? 'call' : 'details');
    setStaffRows([]);
    setDeadline(null);
    setBusy(false);
    setError('');
    setDone(null);
    const previousOverflow = document.body.style.overflow;
    const returnFocus = lastFocus.current;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
      returnFocus?.focus?.();
      endCheckout();
    };
  }, [open, planId, planIsCustom, endCheckout]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => { if (event.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  useEffect(() => {
    if (step === 'done') doneButtonRef.current?.focus();
  }, [step]);

  if (!open || !plan) return null;

  /**
   * Explains a failed save. Our own validation messages are shown as they
   * are; anything else gets the generic permission/network explanation.
   *
   * @param {Error} saveError
   * @param {string} what
   * @returns {string}
   */
  const describe = (saveError, what) => (saveError.isUserFacing
    ? saveError.message
    : describeWriteError(saveError, { what, path: 'posSignupRequests' }));

  /**
   * Details are in: on to the staff step, opened with as many rows as the
   * visitor picked on the pricing card (free + add-ons) the first time.
   * Plans without one go straight to the payment.
   * @returns {void}
   */
  const handleDetailsContinue = () => {
    setError('');
    if (!hasStaffStep) {
      handleProceedToPay([]);
      return;
    }
    if (staffRows.length === 0) {
      const picked = (plan.freeStaff || 0) + (plan.allowAddons ? addons.extraStaff : 0);
      setStaffRows(Array(Math.max(1, Math.min(picked, signupStaffLimit(plan)))).fill(''));
    }
    setStep('staff');
  };

  /**
   * Opens the payment in a new tab with the order in it, and starts the
   * payment window. Runs inside the click: browsers only allow window.open
   * during one.
   * @param {string[]} staffEmails - cleaned, from the staff step
   * @returns {void}
   */
  const handleProceedToPay = (staffEmails) => {
    const payBy = Date.now() + POS_SIGNUP_PAY_WINDOW_SECONDS * 1000;
    setError('');
    checkoutTab.open({
      plan,
      contact,
      staffEmails,
      // Staff add-ons are the named accounts beyond the free ones; the
      // pricing card's count was only the starting number of rows.
      addons: {
        extraStaff: hasStaffStep ? signupPaidStaffCount(plan, staffEmails.length) : 0,
        warehouse: plan.allowAddons ? addons.warehouse : 0,
      },
      prices,
      deadline: payBy,
    });
    setDeadline(payBy);
    setStep('pay');
  };

  /** Backs out of the payment from this dialog: closes the tab. @returns {void} */
  const handlePayBack = () => {
    checkoutTab.end();
    setDeadline(null);
    setStep(hasStaffStep ? 'staff' : 'details');
  };

  /**
   * The payment window ran out with no checkout tab left to report it
   * (closed, or never opened) — end it from here.
   * @returns {void}
   */
  const handlePendingExpire = () => {
    if (checkoutTab.isCheckoutTabOpen()) return;
    checkoutTab.end();
    setDeadline(null);
    setError('Your checkout expired before the receipt was submitted. Continue again when you are ready to pay.');
    setStep(hasStaffStep ? 'staff' : 'details');
  };

  /** Saves the free-trial request — details only, nothing to pay. @returns {Promise<void>} */
  const handleTrialSubmit = async () => {
    setBusy(true);
    setError('');
    try {
      const request = await submitTrialRequest({ plan, contact });
      // Not awaited — the request is saved; the email is a courtesy on top.
      sendSignupReceivedEmail(request, null, { keepalive: true });
      setDone({
        title: 'Free trial request received',
        heading: 'Your free trial is on its way',
        message: 'We have your details. Our admins will set up your account and email your login link shortly — no card, no e-wallet, no auto-charge. Thank you for letting us be part of how your business runs.',
      });
      setContact(BLANK_CONTACT);
      setStep('done');
    } catch (saveError) {
      console.error('Free trial request failed:', saveError);
      setError(describe(saveError, 'your free trial'));
    } finally {
      setBusy(false);
    }
  };

  /**
   * Saves the demo-call booking.
   *
   * @param {{date: Date, slot: object, details: object}} booking
   * @returns {Promise<void>}
   */
  const handleBook = async ({ date, slot, details }) => {
    setBusy(true);
    setError('');
    // Slots after midnight are listed under the evening they follow, but the
    // call happens on the NEXT calendar day — record and quote that day, so
    // "Oct 1, 12:30 AM" never means the night of Oct 1 to 2.
    const dayOffset = Math.floor(slot.start / (24 * 60));
    const callDay = new Date(date.getFullYear(), date.getMonth(), date.getDate() + dayOffset);
    const startOfCall = slot.start % (24 * 60);
    try {
      const request = await submitDemoRequest({
        plan,
        contact: details,
        about: details.about,
        features: details.features,
        callDate: toDateString(callDay),
        slotStart: startOfCall,
        slotLabel: slot.label,
      });
      sendSignupReceivedEmail(request, null, { keepalive: true });
      setDone({
        title: 'Demo call booked',
        heading: 'Your strategy call has been received',
        message: `Your 30-minute demo is set for ${formatLongDate(callDay)} at ${formatClockMinutes(startOfCall)}. We will email a confirmation and a call link, and bring a first look at what your build could include. Thank you for letting us be part of how your business runs.`,
      });
      setStep('done');
    } catch (saveError) {
      console.error('Demo booking failed:', saveError);
      setError(describe(saveError, 'your booking'));
    } finally {
      setBusy(false);
    }
  };

  // The step list, for a paid plan's three steps only.
  const paidSteps = (!planIsTrial && !planIsCustom && ['details', 'staff', 'pay'].includes(step))
    ? PAID_STEPS.filter((entry) => hasStaffStep || entry.key !== 'staff')
    : null;
  const stepIndex = paidSteps ? paidSteps.findIndex((entry) => entry.key === step) : -1;

  // Once the staff step has rows, the summary's staff add-ons are the named
  // accounts beyond the free ones rather than the pricing card's number.
  const typedStaff = staffRows.filter((row) => String(row).trim()).length;
  const summaryAddons = (step === 'staff' || step === 'pay')
    ? { extraStaff: signupPaidStaffCount(plan, typedStaff), warehouse: addons.warehouse }
    : addons;

  const titles = {
    details: planIsTrial ? 'Start your free trial' : 'Subscription details',
    staff: 'Staff accounts',
    pay: 'Complete your payment',
    call: 'Book a 30-minute demo call',
    done: done?.title || '',
  };

  return (
    <div className="modal">
      <div className="modal-bd" />
      <div className="modal-card" role="dialog" aria-modal="true" aria-labelledby="kl-sub-title">
        <div className="modal-head">
          <h3 id="kl-sub-title">{titles[step]}</h3>
          <button type="button" className="modal-x" aria-label="Close" onClick={onClose} disabled={busy}>&times;</button>
        </div>
        <div className="modal-body">
          {paidSteps && (
            <ol className="sub-steps">
              {paidSteps.map((entry, index) => (
                <li key={entry.key} className={index === stepIndex ? 'current' : (index < stepIndex ? 'done' : '')}>
                  <span>{index + 1}</span>
                  {entry.label}
                </li>
              ))}
            </ol>
          )}

          {step !== 'done' && <PosLandingOrderSummary plan={plan} addons={summaryAddons} prices={prices} />}

          {step === 'details' && (
            <PosLandingDetailsForm
              value={contact}
              onChange={setContact}
              onContinue={planIsTrial ? handleTrialSubmit : handleDetailsContinue}
              submitLabel={planIsTrial ? 'Start free trial' : 'Continue'}
              busy={busy}
              saveError={error}
            />
          )}

          {step === 'staff' && (
            <PosLandingStaffStep
              plan={plan}
              prices={prices}
              value={staffRows}
              onChange={setStaffRows}
              onContinue={handleProceedToPay}
              onBack={() => { setError(''); setStep('details'); }}
              error={error}
              onError={setError}
            />
          )}

          {step === 'pay' && (
            // Kawnter's colours on the shared pending card (PosCheckout.css).
            <div className="ps-kawnter">
              <PosCheckoutTabPending
                tabState={checkoutTab.tabState}
                onOpenCheckout={checkoutTab.reopen}
                onBack={handlePayBack}
                submitLabel="Submit subscription"
                backLabel={hasStaffStep ? 'Back to staff accounts' : 'Back to details'}
                deadline={deadline}
                onExpire={handlePendingExpire}
                heading={null}
              />
            </div>
          )}

          {step === 'call' && <PosLandingDemoBooking busy={busy} error={error} onSubmit={handleBook} />}

          {step === 'done' && done && (
            <div className="sub-done">
              <div className="tickring"><PosLandingTick strokeWidth={2.6} /></div>
              <h4>{done.heading}</h4>
              <p>{done.message}</p>
              <button type="button" className="btn btn-ghost btn-lg" ref={doneButtonRef} onClick={onClose}>Close</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PosLandingSubscribeModal;
